/**
 * Shared helpers for song audio uploads.
 *
 * Kept in one place because two screens upload audio (ContestantSubmit and
 * ContestantSongReview) and their error handling must not drift apart.
 *
 * The size ceiling here must stay in step with two other places:
 *   - multer's limits.fileSize in server/routes/songs.js
 *   - client_max_body_size in the foom block of C:\nginx\conf\nginx.conf
 * When those disagreed (nginx 10M vs multer 50MB), nginx rejected uploads with
 * a bare 413 before Node ever saw them, so the app could only show a generic
 * "upload failed" and contestants had no idea the file was simply too big.
 */

export const MAX_AUDIO_BYTES = 50 * 1024 * 1024;   // 50 MB
export const MAX_AUDIO_LABEL = '50 MB';

/**
 * Check a file before uploading, so an oversized file fails instantly and
 * locally instead of after a long doomed upload.
 * @returns {string|null} an error message, or null if the file is acceptable
 */
export function oversizeMessage(file) {
  if (!file || file.size <= MAX_AUDIO_BYTES) return null;
  const mb = (file.size / (1024 * 1024)).toFixed(1);
  return `This file is ${mb} MB, which is over the ${MAX_AUDIO_LABEL} limit. `
       + `Try exporting it at a lower bitrate (192 kbps is fine for judging), then upload again.`;
}

/**
 * Turn an upload failure into something a contestant can act on.
 *
 * A 413 comes from nginx rather than the app, so it has no JSON body to read
 * an error message from — it has to be recognised by status code alone.
 */
export function uploadErrorMessage(err) {
  if (err?.response?.status === 413) {
    return `This file is too large to upload — the maximum is ${MAX_AUDIO_LABEL}. `
         + `Try exporting it at a lower bitrate (192 kbps is fine for judging), then upload again.`;
  }
  return err?.response?.data?.error
      ?? 'Upload failed. Please check your connection and try again.';
}
